import { VoicingCheck } from "./types";

/**
 * Проверка короткой озвучки на слух — то, что о ней знает редактор.
 *
 * fdcde2a1 «Беседа о любви. Часть 5», папка 1, 0:53: голос лектора (клон, eleven_multilingual_v2)
 * читал одно слово «Care» как «каре»; каждый ♪ давал новый случайный дубль, чаще неверный, и
 * переводчику казалось, что «ничего не переозвучивает». Теперь сервер слушает дубль коротких
 * реплик (Scribe), сравнивает слова и переозвучивает до совпадения. Итог — `voicing_check` в
 * ответе ♪ и в json реплики. Не совпало и после всех попыток — нужен переводчик: другое
 * написание или другое слово.
 */

/** Поле `voicing_check` ответа или json реплики, если оно правильной формы; иначе — нет проверки. */
export const voicingCheckOf = (value: unknown): VoicingCheck | undefined => {
    if (!value || typeof value !== "object") return undefined;
    const raw = value as Record<string, unknown>;
    if (raw.ok !== true && raw.ok !== false && raw.ok !== null) return undefined;
    return {
        ok: raw.ok,
        ...(typeof raw.heard === "string" ? { heard: raw.heard } : {}),
        ...(typeof raw.takes === "number" && Number.isFinite(raw.takes) ? { takes: raw.takes } : {}),
    };
};

/** «после 1 попытки», «после 4 попыток», «после 21 попытки» — родительный падеж. */
const afterTakes = (takes: number): string =>
    `после ${takes} ${takes % 10 === 1 && takes % 100 !== 11 ? "попытки" : "попыток"}`;

/**
 * Красная полоса после ♪, когда дубль не совпал с текстом и после всех попыток сервера:
 * «Озвучка реплики 0:53 «Care» после 4 попыток звучит как «खारे» — слово читается неверно;
 * попробуйте другое написание или слово.» `at` — время реплики (formatCueTime).
 */
export const voicingCheckFailure = ({
    at,
    text,
    check,
}: {
    at: string;
    text: string | undefined;
    check: VoicingCheck;
}): string => {
    const shown = (text || "").trim();
    const heard = (check.heard || "").trim();
    const several = shown.split(/\s+/).filter(Boolean).length > 1;
    const parts = [`Озвучка реплики ${at}`];
    if (shown) parts.push(`«${shown}»`);
    if (typeof check.takes === "number" && check.takes > 0) parts.push(afterTakes(check.takes));
    parts.push(heard ? `звучит как «${heard}»` : "звучит без различимых слов");
    const advice = several
        ? "слова читаются неверно; попробуйте другое написание или другие слова."
        : "слово читается неверно; попробуйте другое написание или слово.";
    return `${parts.join(" ")} — ${advice}`;
};

/** Подсказка ♪ строки, пока дубль не совпадает с текстом; иначе null. */
export const voicingCheckWarning = (check: VoicingCheck | undefined | null, en: boolean): string | null => {
    if (!check || check.ok !== false) return null;
    const heard = (check.heard || "").trim();
    if (en) {
        return heard
            ? `the voicing does not match the text: heard «${heard}»`
            : "the voicing does not match the text: no words heard";
    }
    return heard ? `озвучка не совпала с текстом: слышно «${heard}»` : "озвучка не совпала с текстом: слов не слышно";
};
